// ─── 🏆 Tornei ed eventi: il registro della Segreteria ──────────────────────
//
// 🗣️ 28/09/2026 — «Notifiche ai soci: testi e frequenza», mockup «Notifiche ai
// soci» approvato (*«mockup approvato, scelte consigliate, procedi»*). Chi ha
// detto sì a «🏆 Tornei ed eventi» riceve dal bot i tornei e gli stage **adatti
// a lui**: per saperlo, un torneo non può più essere una riga di testo in una
// casella. Deve avere una **data**, i **livelli** per cui è pensato e i **posti**.
//
// ⇒ La Segreteria li pubblica dal gestionale (🏆 Tornei ed eventi) e ci iscrive
// i soci; il bot li legge dal ponte (`GET /players/:memberId/events`), già
// giudicati per quel socio: iscritto o no, posti rimasti, adatto o no.
//
// ⚖️ La casella «Tornei ed eventi» di «Cosa dice il bot ai soci» **resta**: il
// bot la usa ancora in «Domande e info sul circolo», dove serve un testo.
//
// 🎾 **I livelli sono numeri della scala del circolo** (`members.level`,
// `numeric(3,1)`), gli stessi dei soci: la Segreteria li sceglie a parole (le
// fasce), e a parole escono verso il bot (`levelLabel`). Il confronto col socio
// si fa sui numeri, dove non c'è niente da tradurre.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
import { fasciaDiLivelloSchema, parolaDelLivello } from './livelli.js';
export const TIPI_EVENTO = ['torneo', 'stage'];
export const tipoEventoSchema = z.enum(TIPI_EVENTO);
export const ETICHETTE_TIPO_EVENTO = {
    torneo: 'Torneo',
    stage: 'Stage',
};
/** Un livello della scala: da 0 a 10, come la colonna dei soci. */
const livelloSchema = z.number().min(0).max(10);
// ── Per la Segreteria ────────────────────────────────────────────────────────
export const eventoSchema = z.object({
    id: z.uuid(),
    kind: tipoEventoSchema,
    title: z.string(),
    date: dataSchema,
    time: oraSchema.nullable(),
    endDate: dataSchema.nullable(),
    levelMin: z.number().nullable(),
    levelMax: z.number().nullable(),
    /** `null` = senza limite. */
    places: z.number().int().nullable(),
    description: z.string(),
    cancelledAt: z.string().nullable(),
    enrolledCount: z.number().int(),
    /** `null` = senza limite. Mai sotto zero, anche se i posti sono stati abbassati. */
    placesLeft: z.number().int().nullable(),
    /** I livelli a parole, come li legge il socio; `null` = aperto a tutti. */
    levelLabel: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * L'elenco, con la **scala del circolo** accanto: la schermata sceglie i livelli
 * a parole, e leggerla da qui evita di chiedere il permesso dei soci per una
 * pagina che è del calendario.
 */
export const elencoEventiSchema = z.object({
    items: z.array(eventoSchema),
    fasce: z.array(fasciaDiLivelloSchema),
});
const campiEvento = {
    kind: tipoEventoSchema,
    title: z.string().trim().min(1, 'Serve un titolo').max(120),
    date: dataSchema,
    time: oraSchema.nullable(),
    endDate: dataSchema.nullable(),
    levelMin: livelloSchema.nullable(),
    levelMax: livelloSchema.nullable(),
    places: z.number().int().min(1, 'I posti partono da 1: vuoto vuol dire senza limite').max(9999).nullable(),
    description: z.string().trim().max(2000),
};
export const creaEventoSchema = z.object(campiEvento);
/** Si manda solo quello che cambia: ciò che non arriva resta com'è. */
export const modificaEventoSchema = z.object(campiEvento).partial();
export const iscrittoSchema = z.object({
    memberId: z.uuid(),
    displayName: z.string(),
    level: z.number().nullable(),
    /** Il livello a parole, com'è nella scheda; `null` se la scala non lo chiama. */
    levelLabel: z.string().nullable(),
    enrolledAt: z.string(),
});
export const elencoIscrittiSchema = z.object({ items: z.array(iscrittoSchema) });
export const iscriviSchema = z.object({ memberId: z.uuid() });
// ── Per il bot ───────────────────────────────────────────────────────────────
export const eventoDelPonteSchema = z.object({
    id: z.uuid(),
    kind: tipoEventoSchema,
    title: z.string(),
    date: dataSchema,
    time: oraSchema.nullable(),
    endDate: dataSchema.nullable(),
    levelLabel: z.string().nullable(),
    placesLeft: z.number().int().nullable(),
    description: z.string(),
    enrolled: z.boolean(),
    suitable: z.boolean(),
});
export const eventiDelPonteSchema = z.object({ items: z.array(eventoDelPonteSchema) });
// ── Le regole, in un posto solo ──────────────────────────────────────────────
/**
 * Le fasce della scala che un intervallo di livelli tocca, in ordine.
 * ⚖️ «Tocca» e non «contiene»: la Segreteria sceglie «da Intermedio ad
 * Avanzato», e l'intervallo che ne nasce (3,0–4,5) tocca proprio quelle due.
 */
function fasceToccate(fasce, min, max) {
    return fasce.filter((f) => f.a >= min - 1e-9 && f.da <= max + 1e-9);
}
/** «a Avanzato» si dice «ad Avanzato». */
function a(parola) {
    return /^[aA]/.test(parola) ? `ad ${parola}` : `a ${parola}`;
}
/** Il numero con la virgola: solo quando la scala del circolo non ha parole per dirlo. */
function inCifre(n) {
    return n.toFixed(1).replace('.', ',');
}
/**
 * 🎾 **I livelli di un evento, a parole**: «Intermedio», «Intermedio e
 * Avanzato», «da Base ad Agonista», «da Intermedio in su», «fino a Base».
 * `null` quando è aperto a tutti.
 *
 * ⚠️ Se la scala del circolo non chiama quel livello in nessun modo, si dice il
 * numero: dire `null` vorrebbe dire «aperto a tutti», che è falso — e un socio
 * fuori livello si iscriverebbe per niente.
 */
export function livelliDellEvento(fasce, min, max) {
    if (min === null && max === null)
        return null;
    if (max === null)
        return `da ${parolaDelLivello(fasce, min) ?? inCifre(min)} in su`;
    if (min === null)
        return `fino ${a(parolaDelLivello(fasce, max) ?? inCifre(max))}`;
    const toccate = fasceToccate(fasce, min, max);
    if (toccate.length === 0) {
        return min === max ? inCifre(min) : `da ${inCifre(min)} ${a(inCifre(max))}`;
    }
    const prima = toccate[0].parola;
    const ultima = toccate[toccate.length - 1].parola;
    if (toccate.length === 1)
        return prima;
    if (toccate.length === 2)
        return `${prima} e ${ultima}`;
    return `da ${prima} ${a(ultima)}`;
}
/** I posti che restano: `null` senza limite, e mai sotto zero. */
export function postiRimasti(posti, iscritti) {
    return posti === null ? null : Math.max(0, posti - iscritti);
}
/**
 * ⭐ **Un evento è adatto a un socio** se non c'è già, se ha ancora posto e se il
 * suo livello sta dentro quelli dell'evento (gli estremi vuoti non limitano).
 *
 * ⚖️ Chi il livello non l'ha ancora è adatto solo agli eventi **aperti a tutti**:
 * proporgli un torneo per Avanzati vorrebbe dire indovinare, e indovinare un
 * livello è proprio la cosa che il circolo ha deciso di non fare.
 */
export function eventoAdatto(e) {
    if (e.iscritto)
        return false;
    if (e.postiRimasti !== null && e.postiRimasti <= 0)
        return false;
    if (e.levelMin === null && e.levelMax === null)
        return true;
    if (e.livelloDelSocio === null)
        return false;
    if (e.levelMin !== null && e.livelloDelSocio < e.levelMin - 1e-9)
        return false;
    if (e.levelMax !== null && e.livelloDelSocio > e.levelMax + 1e-9)
        return false;
    return true;
}
/**
 * Le regole di un evento intero, prima di scriverlo — nuovo o corretto.
 * Torna la frase per la Segreteria, o `null` se va bene.
 */
export function problemaDellEvento(e) {
    if (e.endDate !== null && e.endDate < e.date)
        return 'La data di fine viene prima di quella di inizio.';
    if (e.levelMin !== null && e.levelMax !== null && e.levelMin > e.levelMax)
        return 'Il livello «da» è più alto del livello «a».';
    return null;
}
//# sourceMappingURL=eventi.js.map